import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "./baseQueryWithReauth";

// Leads tab data.
// - GET /admin/leads (JWT, admin v2): same filters and shape as GET
//   /cowork/leads: { items, total, limit, offset }, max 200 per page, curl
//   fixtures excluded. Read with archived=all and paged until total.
// - GET /cowork/admin/lead-log (JWT): the old blob-shaped view
//   { key, value, version, updatedAt, updatedBy }, kept as the fallback when
//   the new route is missing (404) or fails.
// - PATCH /admin/leads/:legacyId: { status, archived, nextAction,
//   nextActionDate, notes } (whitelist) -> { msg, lead }.
// - POST /admin/leads/:legacyId/to-event -> 201 { msg, legacyId, event },
//   409 { msg, legacyId, eventDbId } when already promoted.

export const ADMIN_LEADS_PAGE_SIZE = 200;
const MAX_PAGES = 25;

export const LEAD_STATUSES = [
  "inquiry",
  "quoted",
  "negotiating",
  "deposit_sent",
  "confirmed",
  "prep_in_progress",
  "event_done",
  "declined",
  "expired",
  "no_response",
];

function epochSeconds(value) {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? Math.round(ms / 1000) : 0;
}

// { items } from GET /admin/leads -> the snapshot shape the tab and its
// localStorage cache already use. Rows keep `id` = legacyId like the old view;
// version is the newest lastUpdated in epoch seconds (the server's rule).
export function adminLeadsToSnapshot(data) {
  const items = Array.isArray(data?.items) ? data.items : [];
  const value = items.map((lead) => ({
    ...(lead ?? {}),
    id: lead?.legacyId ?? lead?.id ?? lead?._id ?? null,
  }));
  const newest = value.reduce((best, row) => {
    const t = epochSeconds(row?.lastUpdated);
    return t > epochSeconds(best?.lastUpdated) ? row : best;
  }, null);
  return {
    value,
    version: epochSeconds(newest?.lastUpdated),
    updatedAt: newest?.lastUpdated ?? null,
    updatedBy: newest?.updatedBy ?? "",
  };
}

// The id the admin lead routes take.
export function leadIdOf(row) {
  return row?.legacyId ?? row?.id ?? row?._id ?? null;
}

const leadLogApi = createApi({
  reducerPath: "leadLogApi",
  baseQuery: prepareBaseQuery(""),
  tagTypes: ["LeadLog"],
  endpoints: (builder) => ({
    getLeadLog: builder.query({
      query: () => ({ url: "/cowork/admin/lead-log", method: "GET" }),
      providesTags: ["LeadLog"],
      keepUnusedDataFor: 300,
    }),
    getLeadLogVersion: builder.query({
      query: () => ({ url: "/cowork/admin/lead-log/version", method: "GET" }),
      providesTags: ["LeadLog"],
      keepUnusedDataFor: 60,
    }),
    getAdminLeads: builder.query({
      async queryFn(_arg, _api, _extra, fetchWithBQ) {
        const items = [];
        const seen = new Set();
        let total = null;
        for (let page = 0; page < MAX_PAGES; page += 1) {
          const res = await fetchWithBQ({
            url: "/admin/leads",
            method: "GET",
            params: {
              archived: "all",
              limit: ADMIN_LEADS_PAGE_SIZE,
              offset: page * ADMIN_LEADS_PAGE_SIZE,
            },
          });
          if (res?.error) return { error: res.error };
          const batch = Array.isArray(res?.data?.items) ? res.data.items : null;
          if (!batch) {
            return {
              error: { status: "BAD_SHAPE", data: { msg: "GET /admin/leads: no items" } },
            };
          }
          // Dedupe by legacyId across pages (the list can shift while paging).
          batch.forEach((lead) => {
            const key = lead?.legacyId ?? lead?.id ?? lead?._id ?? null;
            if (key !== null && seen.has(key)) return;
            if (key !== null) seen.add(key);
            items.push(lead);
          });
          total = Number.isFinite(Number(res?.data?.total))
            ? Number(res.data.total)
            : null;
          const pageLimit = Number(res?.data?.limit) > 0
            ? Number(res.data.limit)
            : ADMIN_LEADS_PAGE_SIZE;
          if (batch.length < pageLimit || batch.length === 0) break;
          if (total !== null && items.length >= total) break;
        }
        return { data: { items, total: total ?? items.length } };
      },
      providesTags: ["LeadLog"],
      keepUnusedDataFor: 300,
    }),
    updateAdminLead: builder.mutation({
      query: ({ legacyId, ...body }) => ({
        url: `/admin/leads/${encodeURIComponent(legacyId ?? "")}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["LeadLog"],
    }),
    createEventFromAdminLead: builder.mutation({
      query: ({ legacyId, ...body }) => ({
        url: `/admin/leads/${encodeURIComponent(legacyId ?? "")}/to-event`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["LeadLog"],
    }),
  }),
});

export const {
  useGetLeadLogQuery,
  useLazyGetLeadLogQuery,
  useGetLeadLogVersionQuery,
  useGetAdminLeadsQuery,
  useUpdateAdminLeadMutation,
  useCreateEventFromAdminLeadMutation,
} = leadLogApi;

export default leadLogApi;
export { leadLogApi };
